"use client";

import {
  ArrowDown,
  Check,
  ChevronLeft,
  Clock3,
  CreditCard,
  GitMerge,
  LifeBuoy,
  LogOut,
  RotateCcw,
  ShieldCheck,
  TriangleAlert,
  UserRound,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { AccountMergeReview } from "@/actions/phone-auth";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Spinner } from "@/components/ui/spinner";

type Decisions = Record<string, "primary" | "secondary">;

type AccountMergeReviewProps = {
  review: AccountMergeReview;
  /** Changes whenever the server rebuilt the preview, which restarts the flow. */
  reviewKey: string;
  busy: boolean;
  error: string | null;
  changedSinceReview: boolean;
  expiresAt: number;
  decisions: Decisions;
  supportCaseId: string | null;
  onDecisionChange: (key: string, choice: "primary" | "secondary") => void;
  onConfirm: () => void;
  onCancel: () => void;
  /** Re-run the same check, for stops that clear on their own. */
  onRecheck: () => void;
  /** The last re-check found the same conditions still in place. */
  recheckUnchanged: boolean;
};

type StepId =
  | "identity"
  | "profile"
  | "activity"
  | "tickets"
  | "money"
  | "confirm";

const STEP_LABEL: Record<StepId, string> = {
  identity: "Accounts",
  profile: "Profile",
  activity: "Activity",
  tickets: "Bookings",
  money: "Money",
  confirm: "Confirm",
};

/**
 * The review a person reads before two Hiive accounts become one.
 *
 * It is a sequence rather than one long page because the six account states the
 * merge has to serve are not the same decision. An empty duplicate deserves one
 * short warning; an account holding tickets and Hiive Money deserves separate,
 * explicit screens for each. The steps below are derived from what the server
 * actually found, so nobody reads a money screen for an account with no money.
 */
export function AccountMergeReviewScreen({
  review,
  reviewKey,
  busy,
  error,
  changedSinceReview,
  expiresAt,
  decisions,
  supportCaseId,
  onDecisionChange,
  onConfirm,
  onCancel,
  onRecheck,
  recheckUnchanged,
}: AccountMergeReviewProps) {
  const blocked = review.blockers.length > 0;
  const hasProfileDecisions =
    review.profile.conflicts.length > 0 ||
    review.profile.filledFromOtherAccount.length > 0;
  const hasActivity = review.classification.durableActivityCount > 0;
  const hasRecords = review.records.length > 0;
  const hasMoney =
    review.money.secondaryAvailableMinor > 0 ||
    review.money.grantCount > 0 ||
    review.paymentMethods.some((method) => method.owner === "secondary");

  // Nothing public and no money moving is the common case: a duplicate made
  // by signing in a different way. That gets one screen and one tick, not a
  // wizard; the separate steps exist for the decisions that need their own.
  const quick = !hasActivity && !hasMoney;

  const steps = useMemo(() => {
    if (quick) return ["confirm"] as StepId[];
    const ordered: StepId[] = ["identity"];
    if (hasProfileDecisions) ordered.push("profile");
    if (hasActivity) ordered.push("activity");
    if (hasRecords) ordered.push("tickets");
    if (hasMoney) ordered.push("money");
    ordered.push("confirm");
    return ordered;
  }, [hasActivity, hasMoney, hasProfileDecisions, hasRecords, quick]);

  const [stepIndex, setStepIndex] = useState(0);
  const [authorshipAcknowledged, setAuthorshipAcknowledged] = useState(false);
  const [valueAcknowledged, setValueAcknowledged] = useState(false);
  const [finalAcknowledged, setFinalAcknowledged] = useState(false);

  // A rebuilt preview is a different set of facts. Send the person back to the
  // start of the affected sequence and drop every acknowledgement, so nothing
  // they agreed to earlier carries over to data they have not seen.
  const lastReviewKey = useRef(reviewKey);
  useEffect(() => {
    if (lastReviewKey.current === reviewKey) return;
    lastReviewKey.current = reviewKey;
    setStepIndex(0);
    setAuthorshipAcknowledged(false);
    setValueAcknowledged(false);
    setFinalAcknowledged(false);
  }, [reviewKey]);

  const [remainingSeconds, setRemainingSeconds] = useState(() =>
    Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000)),
  );
  useEffect(() => {
    const update = () =>
      setRemainingSeconds(
        Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000)),
      );
    update();
    const timer = window.setInterval(update, 1000);
    return () => window.clearInterval(timer);
  }, [expiresAt]);
  const expired = remainingSeconds <= 0;
  const expiringSoon = !expired && remainingSeconds <= 60;

  const step = steps[Math.min(stepIndex, steps.length - 1)] ?? "identity";
  const isLastStep = step === "confirm";
  const requiresAuthorship = hasActivity;
  const requiresValue = hasMoney;
  const canConfirm =
    finalAcknowledged &&
    (!requiresAuthorship || authorshipAcknowledged) &&
    (!requiresValue || valueAcknowledged);

  return (
    <div className="flex w-full flex-col gap-5">
      <Header
        expired={expired}
        expiringSoon={expiringSoon}
        phoneTail={review.secondary.phoneTail}
        remainingSeconds={remainingSeconds}
        state={review.classification.state}
      />

      {quick ? null : (
        <StepRail
          current={step}
          onJumpBack={(index) => setStepIndex(index)}
          steps={steps}
        />
      )}

      {changedSinceReview ? (
        <Callout tone="warning" icon={RotateCcw} role="status">
          Something changed after your first review, so we rebuilt it. Start
          again from the top and check each section before confirming.
        </Callout>
      ) : null}

      {blocked ? (
        <BlockedPanel
          blockers={review.blockers}
          busy={busy}
          caseId={supportCaseId}
          expired={expired}
          onRecheck={onRecheck}
          recheckUnchanged={recheckUnchanged}
        />
      ) : (
        <>
          {step === "identity" ? <IdentityStep review={review} /> : null}
          {step === "profile" ? (
            <ProfileStep
              decisions={decisions}
              onDecisionChange={onDecisionChange}
              review={review}
            />
          ) : null}
          {step === "activity" ? (
            <ActivityStep
              acknowledged={authorshipAcknowledged}
              onAcknowledgedChange={setAuthorshipAcknowledged}
              review={review}
            />
          ) : null}
          {step === "tickets" ? <TicketsStep review={review} /> : null}
          {step === "money" ? (
            <MoneyStep
              acknowledged={valueAcknowledged}
              onAcknowledgedChange={setValueAcknowledged}
              review={review}
            />
          ) : null}
          {step === "confirm" && quick ? (
            <>
              <IdentityStep review={review} />
              {hasProfileDecisions ? (
                <ProfileStep
                  decisions={decisions}
                  onDecisionChange={onDecisionChange}
                  review={review}
                />
              ) : null}
              {hasRecords ? <TicketsStep review={review} /> : null}
            </>
          ) : null}
          {step === "confirm" ? (
            <ConfirmStep
              acknowledged={finalAcknowledged}
              compact={quick}
              onAcknowledgedChange={setFinalAcknowledged}
              review={review}
            />
          ) : null}
        </>
      )}

      {error ? (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}

      <div className="flex flex-col gap-3 pb-1">
        {blocked ? null : isLastStep ? (
          <Button
            className="h-14 w-full rounded-full bg-brand text-base font-semibold text-brand-foreground hover:bg-brand/90 disabled:opacity-45"
            disabled={busy || expired || !canConfirm}
            onClick={onConfirm}
            type="button"
          >
            {busy ? <Spinner className="size-5" /> : "Combine accounts"}
          </Button>
        ) : (
          <Button
            className="h-14 w-full rounded-full bg-brand text-base font-semibold text-brand-foreground hover:bg-brand/90 disabled:opacity-45"
            disabled={
              busy ||
              expired ||
              (step === "activity" && !authorshipAcknowledged) ||
              (step === "money" && !valueAcknowledged)
            }
            onClick={() => setStepIndex((index) => index + 1)}
            type="button"
          >
            Continue
          </Button>
        )}
        {stepIndex > 0 && !blocked ? (
          <button
            className="inline-flex h-10 items-center justify-center gap-2 text-sm text-text-secondary transition-colors hover:text-text-primary disabled:opacity-45 dark:text-white/60 dark:hover:text-white"
            disabled={busy}
            onClick={() => setStepIndex((index) => Math.max(0, index - 1))}
            type="button"
          >
            <ChevronLeft className="size-4" aria-hidden="true" />
            Back
          </button>
        ) : null}
        <button
          className="inline-flex h-10 items-center justify-center gap-2 text-sm text-text-secondary underline underline-offset-4 transition-colors hover:text-text-primary disabled:opacity-45 dark:text-white/60 dark:hover:text-white"
          disabled={busy}
          onClick={onCancel}
          type="button"
        >
          <ShieldCheck className="size-4" aria-hidden="true" />
          Keep accounts separate
        </button>
      </div>
    </div>
  );
}

const STATE_BADGE: Record<
  AccountMergeReview["classification"]["state"],
  string
> = {
  identity_shell: "Empty account",
  profile_incomplete: "Unfinished profile",
  profile_complete_only: "Complete profile",
  active_nonfinancial: "Posts and activity",
  transactional_or_value: "Bookings and money",
  restricted: "Needs support",
};

function Header({
  expired,
  expiringSoon,
  phoneTail,
  remainingSeconds,
  state,
}: {
  expired: boolean;
  expiringSoon: boolean;
  phoneTail: string;
  remainingSeconds: number;
  state: AccountMergeReview["classification"]["state"];
}) {
  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = remainingSeconds % 60;
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex size-10 items-center justify-center rounded-full bg-brand/10 text-brand dark:bg-white/10 dark:text-white">
        <GitMerge className="size-5" aria-hidden="true" />
      </div>
      <h2 className="mt-1 text-xl font-semibold tracking-tight text-text-primary dark:text-white">
        Combine two Hiive accounts?
      </h2>
      <p className="text-sm leading-5 text-text-secondary dark:text-white/60">
        You verified the mobile number ending in {phoneTail}. It already opens
        another account. Review what will move before the two become one.
      </p>
      <div className="mt-1 flex flex-wrap items-center gap-2">
        <span className="rounded-full bg-surface-alt px-2.5 py-1 text-xs font-medium text-text-secondary dark:bg-white/10 dark:text-white/70">
          {STATE_BADGE[state]}
        </span>
        <span
          aria-live={expiringSoon ? "polite" : "off"}
          className={`flex items-center gap-1.5 text-xs tabular-nums ${
            expiringSoon || expired
              ? "font-medium text-destructive"
              : "text-text-tertiary dark:text-white/45"
          }`}
        >
          <Clock3 className="size-3.5" aria-hidden="true" />
          {expired
            ? "This review has expired"
            : `Expires in ${minutes}:${String(seconds).padStart(2, "0")}`}
        </span>
      </div>
    </div>
  );
}

function StepRail({
  current,
  onJumpBack,
  steps,
}: {
  current: StepId;
  onJumpBack: (index: number) => void;
  steps: StepId[];
}) {
  const currentIndex = steps.indexOf(current);
  return (
    <ol className="flex flex-wrap gap-1.5" aria-label="Review steps">
      {steps.map((step, index) => {
        const done = index < currentIndex;
        const active = index === currentIndex;
        return (
          <li key={step}>
            <button
              aria-current={active ? "step" : undefined}
              className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors ${
                active
                  ? "border-brand/30 bg-brand/[0.08] font-semibold text-text-primary dark:text-white"
                  : done
                    ? "border-border text-text-secondary dark:border-white/10 dark:text-white/60"
                    : "border-border text-text-tertiary opacity-55 dark:border-white/10 dark:text-white/40"
              }`}
              disabled={!done}
              onClick={() => onJumpBack(index)}
              type="button"
            >
              {done ? (
                <Check className="size-3" aria-hidden="true" />
              ) : (
                <span aria-hidden="true">{index + 1}</span>
              )}
              {STEP_LABEL[step]}
            </button>
          </li>
        );
      })}
    </ol>
  );
}

function IdentityStep({ review }: { review: AccountMergeReview }) {
  return (
    <>
      <Callout tone="neutral" icon={UserRound}>
        {review.secondary.profileState === "empty"
          ? "The mobile account has no profile details. Only its sign-in methods and the records listed here will move."
          : review.secondary.profileState === "incomplete"
            ? "The mobile account has an unfinished profile. Empty details are filled automatically; conflicting ones are yours to choose."
            : "The mobile account has a completed profile. You will choose between the conflicting details."}
      </Callout>

      <ReviewSection title="Account you will keep">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate font-semibold text-text-primary dark:text-white">
              {review.primary.name}
            </p>
            {review.primary.email ? (
              <p className="mt-0.5 truncate text-sm text-text-secondary dark:text-white/55">
                {review.primary.email}
              </p>
            ) : null}
          </div>
          <Pill tone="success">Kept</Pill>
        </div>

        <div className="flex justify-center py-2">
          <ArrowDown className="size-4 text-text-tertiary" aria-hidden="true" />
        </div>

        <div className="flex items-start justify-between gap-3 border-t border-border pt-3 dark:border-white/10">
          <div>
            <p className="text-sm font-medium text-text-primary dark:text-white">
              Mobile •••• {review.secondary.phoneTail}
            </p>
            {review.secondary.contactEmail ? (
              <p className="mt-0.5 text-xs text-text-secondary dark:text-white/55">
                {review.secondary.contactEmail}
              </p>
            ) : null}
          </div>
          <Pill tone="brand">Added</Pill>
        </div>
      </ReviewSection>

      {review.classification.privilegeScopes.length > 0 ? (
        <Callout icon={ShieldCheck} tone="warning">
          {review.classification.privilegeScopes.join(" and ")} held by the
          mobile account moves to this one. Where both accounts already have the
          same access, the access you have now is the one that is kept.
        </Callout>
      ) : null}

      <ReviewSection title="Sign-in methods">
        <div className="space-y-2 text-sm">
          {review.signInMethods.kept.length > 0 ? (
            <ReviewRow
              label="Stays connected"
              value={review.signInMethods.kept.join(", ")}
            />
          ) : null}
          <ReviewRow
            label="Becomes connected"
            value={review.signInMethods.added.join(", ") || "Mobile OTP"}
          />
        </div>
      </ReviewSection>
    </>
  );
}

function ProfileStep({
  decisions,
  onDecisionChange,
  review,
}: {
  decisions: Decisions;
  onDecisionChange: (key: string, choice: "primary" | "secondary") => void;
  review: AccountMergeReview;
}) {
  return (
    <>
      {review.profile.filledFromOtherAccount.length > 0 ? (
        <ReviewSection title="Filled in from the mobile account">
          <p className="text-sm leading-5 text-text-secondary dark:text-white/60">
            These were empty here, so the mobile account's values are used:{" "}
            {review.profile.filledFromOtherAccount.join(", ")}.
          </p>
        </ReviewSection>
      ) : null}

      {review.profile.conflicts.length > 0 ? (
        <ReviewSection title="Choose the details to keep">
          {review.secondary.profileState === "complete" &&
          review.profile.conflicts.some(
            (conflict) => conflict.selected === "secondary",
          ) ? (
            <p className="mb-3 text-sm leading-5 text-text-secondary dark:text-white/60">
              The mobile account has your finished profile, so its details are
              picked. Change any of them below.
            </p>
          ) : null}
          <div className="space-y-4">
            {review.profile.conflicts.map((conflict) => {
              const selected = decisions[conflict.key] ?? conflict.selected;
              return (
                <fieldset key={conflict.key} className="space-y-2">
                  <legend className="text-sm font-semibold text-text-primary dark:text-white">
                    {conflict.label}
                  </legend>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {(
                      [
                        ["primary", "Current account", conflict.primaryValue],
                        [
                          "secondary",
                          "Mobile account",
                          conflict.secondaryValue,
                        ],
                      ] as const
                    ).map(([choice, source, value]) => (
                      <label
                        key={choice}
                        className={`flex cursor-pointer items-start gap-2.5 rounded-xl border p-3 ${
                          selected === choice
                            ? "border-brand bg-brand/[0.06]"
                            : "border-border dark:border-white/10"
                        }`}
                      >
                        <input
                          checked={selected === choice}
                          className="mt-0.5 accent-brand"
                          name={`merge-${conflict.key}`}
                          onChange={() =>
                            onDecisionChange(conflict.key, choice)
                          }
                          type="radio"
                          value={choice}
                        />
                        <span className="min-w-0">
                          <span className="block text-xs font-medium text-text-tertiary dark:text-white/45">
                            {source}
                          </span>
                          <span className="mt-0.5 block text-sm break-words text-text-primary dark:text-white">
                            {value}
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              );
            })}
          </div>
          <p className="mt-3 border-t border-border pt-3 text-xs leading-5 text-text-tertiary dark:border-white/10 dark:text-white/50">
            {review.profile.privacy}
          </p>
        </ReviewSection>
      ) : null}
    </>
  );
}

function ActivityStep({
  acknowledged,
  onAcknowledgedChange,
  review,
}: {
  acknowledged: boolean;
  onAcknowledgedChange: (value: boolean) => void;
  review: AccountMergeReview;
}) {
  const activityCategories = review.dataToMove.filter(
    (item) =>
      item.category !== "Profile and settings" &&
      item.category !== "Other account data" &&
      item.category !== "Wallet and payments",
  );
  return (
    <>
      <ReviewSection title="Public posts and activity">
        <p className="text-sm leading-5 text-text-secondary dark:text-white/60">
          The mobile account has{" "}
          <strong className="font-semibold text-text-primary tabular-nums dark:text-white">
            {review.classification.durableActivityCount}
          </strong>{" "}
          {review.classification.durableActivityCount === 1
            ? "post, comment, session or workout"
            : "posts, comments, sessions and workouts"}
          . All of it moves across and keeps its original dates.
        </p>
        {activityCategories.length > 0 ? (
          <ul className="mt-3 space-y-2 border-t border-border pt-3 dark:border-white/10">
            {activityCategories.map((item) => (
              <li
                key={item.category}
                className="flex items-center justify-between gap-4 text-sm"
              >
                <span className="text-text-secondary dark:text-white/65">
                  {item.category}
                </span>
                <span className="font-semibold text-text-primary tabular-nums dark:text-white">
                  {item.count}
                </span>
              </li>
            ))}
          </ul>
        ) : null}
        {review.derivedDataRebuilt.length > 0 ? (
          <p className="mt-3 border-t border-border pt-3 text-xs leading-5 text-text-secondary dark:border-white/10 dark:text-white/55">
            {review.derivedDataRebuilt.join(" and ")} will be recalculated from
            the combined history after the merge.
          </p>
        ) : null}
      </ReviewSection>

      <AcknowledgeBox
        checked={acknowledged}
        label="Acknowledge that public content moves"
        onChange={onAcknowledgedChange}
      >
        I understand that everything the mobile account posted will appear under
        the profile above, and that deleting individual posts is a separate step
        afterwards.
      </AcknowledgeBox>
    </>
  );
}

const RECORD_GROUP: Record<
  AccountMergeReview["records"][number]["kind"],
  string
> = {
  event_booking: "Event bookings",
  event_ticket: "Tickets assigned to the mobile account",
  venue_booking: "Venue bookings",
  shop_order: "Shop orders",
  membership: "Memberships",
};

function TicketsStep({ review }: { review: AccountMergeReview }) {
  const groups = new Map<string, AccountMergeReview["records"]>();
  for (const record of review.records) {
    const key = RECORD_GROUP[record.kind];
    groups.set(key, [...(groups.get(key) ?? []), record]);
  }
  return (
    <>
      {[...groups.entries()].map(([title, records]) => (
        <ReviewSection key={title} title={title}>
          <ul className="space-y-2.5">
            {records.map((record) => (
              <li
                key={`${record.kind}-${record.id}`}
                className="rounded-xl border border-border bg-surface-alt p-3 dark:border-white/10 dark:bg-white/[0.04]"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-text-primary dark:text-white">
                      {record.label}
                    </p>
                    {record.detail ? (
                      <p className="mt-0.5 text-xs text-text-secondary dark:text-white/55">
                        {record.detail}
                      </p>
                    ) : null}
                    <p className="mt-0.5 text-xs text-text-tertiary capitalize dark:text-white/45">
                      {record.role === "organised"
                        ? "Organised by the mobile account"
                        : record.role === "assigned"
                          ? "Assigned to the mobile account"
                          : "Booked by the mobile account"}
                      {record.status
                        ? ` · ${record.status.replaceAll("_", " ")}`
                        : ""}
                    </p>
                  </div>
                  {record.amountMinor !== null ? (
                    <span className="shrink-0 text-sm font-semibold text-text-primary tabular-nums dark:text-white">
                      {formatMinor(record.amountMinor, record.currency)}
                    </span>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        </ReviewSection>
      ))}
      <p className="px-1 text-xs leading-5 text-text-secondary dark:text-white/55">
        These keep their booking IDs, ticket codes, payment state and provider
        references. Only the Hiive account that owns them changes.
      </p>
    </>
  );
}

function MoneyStep({
  acknowledged,
  onAcknowledgedChange,
  review,
}: {
  acknowledged: boolean;
  onAcknowledgedChange: (value: boolean) => void;
  review: AccountMergeReview;
}) {
  const { money } = review;
  const currency = money.currency;
  const provenance = [
    ["Bought with money", money.movingCustomerFundedMinor],
    ["Given by Hiive", money.movingPlatformFundedMinor],
    ["From a session split", money.movingPeerFundedMinor],
  ].filter(([, amount]) => (amount as number) > 0) as [string, number][];
  const secondaryMethods = review.paymentMethods.filter(
    (method) => method.owner === "secondary",
  );

  return (
    <>
      <ReviewSection title="Hiive Money">
        <div className="space-y-2 text-sm">
          <ReviewRow
            label="This account can spend"
            value={formatMinor(money.primaryAvailableMinor, currency)}
          />
          <ReviewRow
            label="The mobile account can spend"
            value={formatMinor(money.secondaryAvailableMinor, currency)}
          />
          <div className="border-t border-border pt-2 dark:border-white/10">
            <ReviewRow
              emphasis
              label="Combined balance after merging"
              value={formatMinor(money.combinedAvailableMinor, currency)}
            />
          </div>
        </div>
        {provenance.length > 0 ? (
          <div className="mt-3 border-t border-border pt-3 dark:border-white/10">
            <p className="mb-2 text-sm font-medium text-text-primary dark:text-white">
              Where the moving value came from
            </p>
            <div className="space-y-1.5 text-sm">
              {provenance.map(([label, amount]) => (
                <ReviewRow
                  key={label}
                  label={label}
                  value={formatMinor(amount, currency)}
                />
              ))}
            </div>
          </div>
        ) : null}
        <p className="mt-3 text-xs leading-5 text-text-secondary dark:text-white/55">
          The whole ledger moves with its original provenance and expiry dates.
          Nothing is re-minted, rounded, cashed out, or discarded, and there is
          no option to move only part of it.
        </p>
      </ReviewSection>

      {secondaryMethods.length > 0 ? (
        <ReviewSection title="Saved payment methods moving">
          <ul className="space-y-1.5 text-sm">
            {secondaryMethods.map((method) => (
              <li
                key={`${method.label}-${method.detail ?? ""}`}
                className="flex items-center gap-2 text-text-secondary dark:text-white/65"
              >
                <CreditCard className="size-4 shrink-0" aria-hidden="true" />
                <span>{method.label}</span>
                {method.detail ? (
                  <span className="text-xs text-text-tertiary dark:text-white/45">
                    {method.detail}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </ReviewSection>
      ) : null}

      <AcknowledgeBox
        checked={acknowledged}
        label="Acknowledge the stored value moving"
        onChange={onAcknowledgedChange}
      >
        I understand that the mobile account's Hiive Money and saved payment
        methods move to the account above, and that this cannot be undone from
        the app.
      </AcknowledgeBox>
    </>
  );
}

function ConfirmStep({
  acknowledged,
  compact,
  onAcknowledgedChange,
  review,
}: {
  acknowledged: boolean;
  /** The sections above already say what moves; skip the recap. */
  compact: boolean;
  onAcknowledgedChange: (value: boolean) => void;
  review: AccountMergeReview;
}) {
  return (
    <>
      {compact ? null : (
        <ReviewSection title="What happens when you confirm">
          <ul className="space-y-2 text-sm">
            <SummaryLine
              label="Sign-in methods"
              value={review.signInMethods.added.join(", ") || "Mobile OTP"}
            />
            {review.profile.keptFromThisAccount.length > 0 ? (
              <SummaryLine
                label="Profile kept from this account"
                value={review.profile.keptFromThisAccount.join(", ")}
              />
            ) : null}
            {review.profile.filledFromOtherAccount.length > 0 ? (
              <SummaryLine
                label="Profile filled from the mobile account"
                value={review.profile.filledFromOtherAccount.join(", ")}
              />
            ) : null}
            {review.classification.durableActivityCount > 0 ? (
              <SummaryLine
                label="Posts and activity moving"
                value={String(review.classification.durableActivityCount)}
              />
            ) : null}
            {review.records.length > 0 ? (
              <SummaryLine
                label="Bookings, tickets and orders moving"
                value={String(review.records.length)}
              />
            ) : null}
            {review.money.combinedAvailableMinor > 0 ? (
              <SummaryLine
                label="Combined Hiive Money"
                value={formatMinor(
                  review.money.combinedAvailableMinor,
                  review.money.currency,
                )}
              />
            ) : null}
            <SummaryLine
              label="Other signed-in sessions"
              value={
                review.sessionsRevoked === 0
                  ? "None"
                  : `${review.sessionsRevoked} signed out`
              }
            />
          </ul>
        </ReviewSection>
      )}

      {review.discarded.length > 0 ? (
        <ReviewSection title="What is removed or replaced" tone="warning">
          <ul className="space-y-3">
            {review.discarded.map((item) => (
              <li
                key={`${item.label}-${item.reason}-${item.count}`}
                className="text-sm"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="font-medium text-text-primary dark:text-white">
                    {item.label}
                  </span>
                  <span className="text-text-secondary tabular-nums dark:text-white/55">
                    {item.count}
                  </span>
                </div>
                <p className="mt-0.5 text-xs leading-4 text-text-secondary dark:text-white/55">
                  {item.reason}
                </p>
              </li>
            ))}
          </ul>
        </ReviewSection>
      ) : null}

      <AcknowledgeBox
        checked={acknowledged}
        label="Acknowledge account merge"
        onChange={onAcknowledgedChange}
      >
        I understand that the mobile account will no longer open on its own, and
        that its sign-in methods and data move to the account shown above.
      </AcknowledgeBox>
    </>
  );
}

function BlockedPanel({
  blockers,
  busy,
  caseId,
  expired,
  onRecheck,
  recheckUnchanged,
}: {
  blockers: string[];
  busy: boolean;
  caseId: string | null;
  expired: boolean;
  onRecheck: () => void;
  recheckUnchanged: boolean;
}) {
  return (
    <div className="flex flex-col gap-4">
      <div
        className="rounded-2xl border border-warning/30 bg-warning-bg/60 p-4"
        role="alert"
      >
        <div className="flex gap-3">
          <TriangleAlert
            className="mt-0.5 size-5 shrink-0 text-warning"
            aria-hidden="true"
          />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-text-primary dark:text-white">
              Not yet — one thing is in the way
            </p>
            {/*
              This screen used to open with "These accounts need a support
              review", which told the person the outcome (talk to a human) before
              the reason, and gave them nothing to do. Most stops here are a
              condition with a clock on it, so the reason leads and the exit is a
              button rather than an email address.
            */}
            <p className="mt-1 text-sm leading-5 text-text-secondary dark:text-white/65">
              Nothing has changed on either account, and your mobile number
              stays verified while this is sorted out.
            </p>
            <ul className="mt-3 space-y-2">
              {blockers.map((blocker) => (
                <li
                  className="flex gap-2 text-sm leading-5 text-text-primary dark:text-white/85"
                  key={blocker}
                >
                  <span
                    aria-hidden="true"
                    className="mt-[0.45rem] size-1.5 shrink-0 rounded-full bg-warning"
                  />
                  <span>{blocker}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {recheckUnchanged ? (
        <Callout icon={Clock3} role="status" tone="warning">
          Still the same when we looked just now. Most of these clear on their
          own within a day — come back and check again, or contact support if it
          stays.
        </Callout>
      ) : null}

      {/*
        Disabled once the review expires rather than left clickable: the server
        rejects an expired intent with a generic "we could not prepare the
        review yet, retry without another code", which is not true of an intent
        that has run out. The header above already says it has expired.
      */}
      <Button
        className="h-14 w-full rounded-full bg-brand text-base font-semibold text-brand-foreground hover:bg-brand/90 disabled:opacity-45"
        disabled={busy || expired}
        onClick={onRecheck}
        type="button"
      >
        {busy ? <Spinner className="size-5" /> : "Check again"}
      </Button>

      <div className="rounded-2xl border border-border bg-surface-alt p-4 dark:border-white/10 dark:bg-white/[0.04]">
        <p className="flex items-center gap-1.5 text-sm font-medium text-text-primary dark:text-white">
          <LifeBuoy className="size-4 shrink-0" aria-hidden="true" />
          If it does not clear
        </p>
        <p className="mt-1 text-sm leading-5 text-text-secondary dark:text-white/65">
          {caseId ? (
            <>
              Support already has this. Quote{" "}
              <span className="font-mono font-semibold text-text-primary dark:text-white">
                {caseId.slice(0, 8).toUpperCase()}
              </span>{" "}
              and they can pick up exactly where you left off — you will not
              have to verify your number again.
            </>
          ) : (
            "Contact support and they can pick up exactly where you left off."
          )}
        </p>
      </div>
    </div>
  );
}

/** The receipt a person keeps after the merge, and after a retried request. */
export function AccountMergeReceiptScreen({
  onDone,
  phoneNumber,
  receiptId,
  repairsPending,
  review,
}: {
  onDone: () => void;
  phoneNumber: string;
  receiptId: string;
  /** Rebuilds the server says are still running, not a guess from the preview. */
  repairsPending: number;
  review: AccountMergeReview;
}) {
  return (
    <div className="flex w-full flex-col gap-5">
      <div className="flex flex-col gap-1.5">
        <div className="flex size-10 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-300">
          <Check className="size-5" aria-hidden="true" />
        </div>
        <h2 className="mt-1 text-xl font-semibold tracking-tight text-text-primary dark:text-white">
          Your accounts are now one
        </h2>
        <p className="text-sm leading-5 text-text-secondary dark:text-white/60">
          Reference {receiptId.slice(0, 8).toUpperCase()}. Keep it if you ever
          need to ask support about this merge.
        </p>
      </div>

      <ReviewSection title="How you sign in now">
        <div className="space-y-2 text-sm">
          <ReviewRow
            label="Methods"
            value={
              [...review.signInMethods.kept, ...review.signInMethods.added]
                .filter((value, index, all) => all.indexOf(value) === index)
                .join(", ") || "Mobile OTP"
            }
          />
          <ReviewRow label="Mobile" value={phoneNumber} />
          {review.primary.email ? (
            <ReviewRow label="Contact email" value={review.primary.email} />
          ) : null}
        </div>
      </ReviewSection>

      <ReviewSection title="What moved">
        <ul className="space-y-2 text-sm">
          {review.dataToMove.map((item) => (
            <SummaryLine
              key={item.category}
              label={item.category}
              value={String(item.count)}
            />
          ))}
          {review.records.length > 0 ? (
            <SummaryLine
              label="Bookings, tickets and orders"
              value={String(review.records.length)}
            />
          ) : null}
          {review.money.combinedAvailableMinor > 0 ? (
            <SummaryLine
              label="Hiive Money balance"
              value={formatMinor(
                review.money.combinedAvailableMinor,
                review.money.currency,
              )}
            />
          ) : null}
          <SummaryLine
            label="Other sessions signed out"
            value={String(review.sessionsRevoked)}
          />
        </ul>
      </ReviewSection>

      {repairsPending > 0 ? (
        <Callout icon={RotateCcw} role="status" tone="neutral">
          {review.derivedDataRebuilt.length > 0
            ? `${review.derivedDataRebuilt.join(" and ")} ${review.derivedDataRebuilt.length === 1 ? "is" : "are"} still being recalculated from your combined history.`
            : "One background rebuild is still running."}{" "}
          Everything else is ready now, and it retries on its own.
        </Callout>
      ) : null}

      <Callout tone="neutral" icon={LogOut}>
        The other account can no longer be signed into on its own. Its audit
        history is kept for security, and support can still see this merge.
      </Callout>

      <Button
        className="h-14 w-full rounded-full bg-brand text-base font-semibold text-brand-foreground hover:bg-brand/90"
        onClick={onDone}
        type="button"
      >
        Done
      </Button>
    </div>
  );
}

function AcknowledgeBox({
  checked,
  children,
  label,
  onChange,
}: {
  checked: boolean;
  children: React.ReactNode;
  label: string;
  onChange: (value: boolean) => void;
}) {
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-border p-4 dark:border-white/10">
      <Checkbox
        aria-label={label}
        checked={checked}
        className="mt-0.5"
        onCheckedChange={(value) => onChange(value === true)}
      />
      <span className="text-sm leading-5 text-text-secondary dark:text-white/65">
        {children}
      </span>
    </div>
  );
}

function Callout({
  children,
  icon: Icon,
  role,
  tone,
}: {
  children: React.ReactNode;
  icon: typeof RotateCcw;
  role?: "status";
  tone: "neutral" | "warning";
}) {
  return (
    <div
      className={
        tone === "warning"
          ? "flex gap-3 rounded-2xl border border-amber-500/25 bg-amber-500/10 p-3 text-amber-950 dark:text-amber-100"
          : "flex gap-3 rounded-2xl border border-border bg-surface-alt p-3 text-text-secondary dark:border-white/10 dark:bg-white/[0.04] dark:text-white/60"
      }
      role={role}
    >
      <Icon className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
      <p className="text-sm leading-5">{children}</p>
    </div>
  );
}

function ReviewSection({
  children,
  title,
  tone = "default",
}: {
  children: React.ReactNode;
  title: string;
  tone?: "default" | "warning";
}) {
  return (
    <section
      className={
        tone === "warning"
          ? "rounded-2xl border border-amber-500/20 bg-amber-500/[0.06] p-4"
          : "rounded-2xl border border-border p-4 dark:border-white/10"
      }
    >
      <h3 className="mb-3 text-sm font-semibold text-text-primary dark:text-white">
        {title}
      </h3>
      {children}
    </section>
  );
}

function ReviewRow({
  emphasis,
  label,
  value,
}: {
  emphasis?: boolean;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-start justify-between gap-4 text-sm">
      <span className="text-text-secondary dark:text-white/60">{label}</span>
      <span
        className={`text-right tabular-nums dark:text-white ${
          emphasis
            ? "text-base font-semibold text-text-primary"
            : "font-medium text-text-primary"
        }`}
      >
        {value}
      </span>
    </div>
  );
}

function SummaryLine({ label, value }: { label: string; value: string }) {
  return (
    <li className="flex items-start justify-between gap-4">
      <span className="text-text-secondary dark:text-white/65">{label}</span>
      <span className="text-right font-semibold text-text-primary tabular-nums dark:text-white">
        {value}
      </span>
    </li>
  );
}

function Pill({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone: "brand" | "success";
}) {
  return (
    <span
      className={
        tone === "success"
          ? "rounded-full bg-emerald-500/10 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:text-emerald-300"
          : "rounded-full bg-brand/10 px-2.5 py-1 text-xs font-semibold text-brand dark:bg-white/10 dark:text-white"
      }
    >
      {children}
    </span>
  );
}

function formatMinor(amountMinor: number, currency: string | null): string {
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: currency ?? "INR",
      maximumFractionDigits: 2,
    }).format(amountMinor / 100);
  } catch {
    return `${currency ?? "INR"} ${(amountMinor / 100).toFixed(2)}`;
  }
}
